/**
 * The waitlist modal.
 *
 * Every "get the app" button on the site opens this, because the app is not out
 * yet. When it ships, the buttons become store links and this component and its
 * route stay for anyone still arriving early.
 *
 * Built on the native <dialog> element. `showModal()` gives us, from the
 * browser and not from our code:
 *   - focus moved into the dialog and trapped there,
 *   - Esc closing it,
 *   - the rest of the page marked inert, so screen readers and Tab cannot
 *     wander out of it,
 *   - the ::backdrop pseudo-element and the top layer, so no z-index fights.
 * A hand-rolled modal has to reimplement all of that and usually gets the focus
 * handling wrong.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { WaitlistErrorCode } from '../../shared/waitlist';

import { useI18n } from '../i18n';
import { submitWaitlist } from '../lib/waitlistApi';
import { Button } from './Button';
import styles from './WaitlistModal.module.css';

type Status = 'idle' | 'submitting' | 'success' | 'error';

interface WaitlistModalProps {
  open: boolean;
  onClose: () => void;
  /** Which button opened it. Travels into the notification email. */
  source: string;
}

export function WaitlistModal({ open, onClose, source }: WaitlistModalProps) {
  const { t, locale, localePath } = useI18n();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [email, setEmail] = useState('');
  const [company, setCompany] = useState(''); // honeypot
  const [status, setStatus] = useState<Status>('idle');
  const [errorCode, setErrorCode] = useState<WaitlistErrorCode | 'network' | null>(null);

  // Drive the dialog from the `open` prop. showModal() is imperative, so this
  // is one of the few places a ref is the right tool rather than a workaround.
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (open && !dialog.open) {
      dialog.showModal();
      // Reset so reopening after a success shows the form again.
      setStatus('idle');
      setErrorCode(null);
      setEmail('');
      setCompany('');
    } else if (!open && dialog.open) {
      dialog.close();
    }
  }, [open]);

  // The browser can close a dialog without us (Esc, or the form's method=dialog),
  // so listen for `close` and keep the parent's state in step.
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const handleClose = () => onClose();
    dialog.addEventListener('close', handleClose);
    return () => dialog.removeEventListener('close', handleClose);
  }, [onClose]);

  /**
   * Close when the backdrop is clicked.
   *
   * A click on the backdrop reports the <dialog> itself as the target, because
   * the backdrop is a pseudo-element and has no node of its own. Anything
   * inside the panel reports that element instead — which is why this check is
   * enough and no coordinate maths is needed.
   */
  const handleDialogClick = useCallback((event: React.MouseEvent<HTMLDialogElement>) => {
    if (event.target === dialogRef.current) {
      dialogRef.current?.close();
    }
  }, []);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (status === 'submitting') return;

    setStatus('submitting');
    setErrorCode(null);

    const result = await submitWaitlist({ email, locale, source, company });

    if (result.ok) {
      setStatus('success');
      return;
    }

    setStatus('error');
    setErrorCode(result.error);
    // Put the cursor back where the fix has to happen.
    requestAnimationFrame(() => inputRef.current?.focus());
  }

  const errorMessage = errorCode
    ? (t.waitlist.errors[errorCode] ?? t.waitlist.errors.server_error)
    : null;

  return (
    <dialog
      ref={dialogRef}
      className={styles.dialog}
      onClick={handleDialogClick}
      aria-labelledby="waitlist-title"
    >
      <div className={styles.panel}>
        <button
          type="button"
          className={styles.close}
          onClick={onClose}
          aria-label={t.waitlist.close}
        >
          &times;
        </button>

        {status === 'success' ? (
          <>
            <div className={styles.successMark} aria-hidden="true">
              ✓
            </div>
            <h2 id="waitlist-title" className={styles.title}>
              {t.waitlist.successTitle}
            </h2>
            {/* role="status" makes a screen reader announce this without
                stealing focus from wherever the user already is. */}
            <p className={styles.body} role="status">
              {t.waitlist.successBody}
            </p>
            <Button variant="primary" size="lg" block onClick={onClose}>
              {t.waitlist.successDone}
            </Button>
          </>
        ) : (
          <>
            <span className={`overline ${styles.badge}`}>{t.waitlist.badge}</span>
            <h2 id="waitlist-title" className={styles.title}>
              {t.waitlist.title}
            </h2>
            <p className={styles.body}>{t.waitlist.body}</p>

            <form className={styles.form} onSubmit={handleSubmit} noValidate>
              <label className={styles.label} htmlFor="waitlist-email">
                {t.waitlist.emailLabel}
              </label>

              <input
                ref={inputRef}
                id="waitlist-email"
                className={styles.input}
                type="email"
                name="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder={t.waitlist.emailPlaceholder}
                required
                autoComplete="email"
                inputMode="email"
                /* Tells the browser and any assistive tech that this field is
                   the one to fix, and links it to the message below. */
                aria-invalid={status === 'error'}
                aria-describedby={errorMessage ? 'waitlist-error' : undefined}
                disabled={status === 'submitting'}
              />

              {/* Honeypot — see .honeypot in the stylesheet. */}
              <input
                type="text"
                name="company"
                className={styles.honeypot}
                value={company}
                onChange={(event) => setCompany(event.target.value)}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
              />

              {errorMessage ? (
                /* aria-live so the message is announced when it appears, not
                   only when something moves focus onto it. */
                <p className={styles.error} id="waitlist-error" role="alert">
                  {errorMessage}
                </p>
              ) : null}

              <Button
                type="submit"
                variant="primary"
                size="lg"
                block
                disabled={status === 'submitting'}
              >
                {status === 'submitting' ? t.waitlist.submitting : t.waitlist.submit}
              </Button>
            </form>

            <p className={styles.privacyNote}>
              {t.waitlist.privacyNote}{' '}
              <Link to={localePath('/privacy')}>{t.waitlist.privacyLink}</Link>
            </p>
          </>
        )}
      </div>
    </dialog>
  );
}
